import { type Maybe, rd } from '@passionware/monads';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkspaceScoping } from 'v5/api/_common/query/workspace-scoping';
import type { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import { getDimmedContainerClassName } from 'v4/shared/components/forms/input/primitvies/DimmedContainer';
import { Button } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';
import { EntityListToolbar } from 'v5/patterns/entity-list/EntityListToolbar';
import { ViewSwitch } from 'v5/patterns/entity-list/ViewSwitch';
import type {
  FilterGroupDefinition,
  PerFieldConfigBase
} from 'v5/patterns/filters/FilterWidget.types';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import {
  RESULTS_PAGE_SIZE,
  ResourcePickerResults,
  type ResourcePickerView
} from './ResourcePickerResults';
import { ResourcePickerActions } from './ResourcePickerActions';
import { ResourcePickerCommit } from './ResourcePickerCommit';
import { scopedResourceQuery } from './scopedResourceQuery';

/**
 * Search, filters and the result count of a picker; the filters are the host's (the resource
 * filters of its scope), so are the ones always shown. Archived resources are never offered, and
 * in a workspace's scope there is no workspace filter: the scope decides whose resources show.
 */
function ResourcePickerToolbar<
  Q extends ResourceSearchQuery,
  TFilterConfig extends PerFieldConfigBase
>({
  query,
  onQueryChange,
  scope,
  filters
}: {
  query: Q;
  onQueryChange: (query: Q) => void;
  scope: Maybe<WorkspaceScoping>;
  filters: {
    config: TFilterConfig;
    groups: FilterGroupDefinition[];
    order: (keyof TFilterConfig)[];
    suggested?: (keyof TFilterConfig)[];
    /** Kept to the host's own: by default `archived`, and the workspace filter in a workspace. */
    hidden?: string[];
  };
}) {
  return (
    <EntityListToolbar
      query={query}
      onQueryChange={onQueryChange}
      filterConfig={filters.config}
      groups={filters.groups}
      order={filters.order}
      suggestedFilters={filters.suggested}
      hiddenFilters={
        filters.hidden ??
        (typeof scope?.workspaceId === 'number'
          ? ['archived', 'enterprise_workspace_id']
          : ['archived'])
      }
      paginationInline={false}
    />
  );
}

/** Above the results: the `Toolbar`, then the `Summary`. */
function ResourcePickerHeader({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-border-default p-3">
      {children}
    </div>
  );
}

/** The row under the toolbar: the `Count` and the host's switches, its view controls at the end. */
function ResourcePickerSummary({
  children,
  actions
}: {
  children: ReactNode;
  /** The result fields and the `ViewSwitch`. */
  actions: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 @container/toolbar">
      {children}
      <span className="ml-auto flex items-center gap-1">{actions}</span>
    </div>
  );
}

/** The List / Grid switch bound to the picker's view; the picker's grid is its gallery. */
function ResourcePickerViewSwitch({
  view,
  onViewChange
}: {
  view: ResourcePickerView;
  onViewChange: (view: ResourcePickerView) => void;
}) {
  return (
    <ViewSwitch
      size="sm"
      value={view === 'gallery' ? 'grid' : 'list'}
      onChange={next => onViewChange(next === 'grid' ? 'gallery' : 'list')}
    />
  );
}

/** How many results the search has; read from the list's first page, which the list reads too. */
function ResourcePickerCount({
  query,
  scope,
  services
}: {
  query: Maybe<ResourceSearchQuery>;
  scope: Maybe<WorkspaceScoping>;
} & WithServices<[WithResourceService]>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const [first] = services.resourceService.useListPages(
    scopedResourceQuery(query, scope),
    [0],
    RESULTS_PAGE_SIZE
  );
  // as the results do: the last search's count, dimmed, until the new one comes
  const countRd = rd.useLastWithPlaceholder(
    first ? rd.map(first, page => page.count) : rd.ofIdle()
  );
  const count = rd.tryGet(countRd);
  return (
    <span
      className={cn(
        'text-sm text-fg-muted',
        getDimmedContainerClassName(rd.isPlaceholderData(countRd))
      )}
      aria-live="polite"
    >
      {count === undefined ? ' ' : t('count', { count })}
    </span>
  );
}

/**
 * The open result's details as a column beside the results, where the host has the room (a
 * dialog): as wide as the split view's sheet where the dialog allows, never under 340px; narrower
 * than `md` it covers the results while a result is open, with a way back, and is gone otherwise.
 */
function ResourcePickerInspector({
  children,
  onClose
}: {
  children?: ReactNode;
  /** Closes the open result: the way back from the cover on a narrow screen. */
  onClose: () => void;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  return (
    <aside
      aria-label={t('details')}
      className={cn(
        'flex w-[clamp(340px,40%,480px)] shrink-0 flex-col overflow-y-auto border-l border-border-default max-md:absolute max-md:inset-0 max-md:z-10 max-md:w-auto max-md:border-l-0 max-md:bg-bg-default',
        !children && 'max-md:hidden'
      )}
    >
      {children ? (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="m-2 mb-0 self-start md:hidden"
            onClick={onClose}
          >
            <ArrowLeft />
            {t('backToResults')}
          </Button>
          {children}
        </>
      ) : (
        <p className="m-auto max-w-56 p-6 text-center text-sm text-fg-muted">
          {t('noneOpen')}
        </p>
      )}
    </aside>
  );
}

/**
 * The footer's row of a picker: how many are picked, Clear, and the host's actions at the end
 * (`Commit`, a dialog's `Cancel`). Always shown, so where the pick goes is in sight; with nobody
 * picked Clear is off. The host's footer (a dialog's, a column's) frames it.
 */
function ResourcePickerPickedFooter({
  count,
  onClear,
  children
}: {
  count: number;
  onClear: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  return (
    <>
      <span className="text-sm text-fg-muted" aria-live="polite">
        {t('picked', { count })}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={count === 0}
        onClick={onClear}
      >
        {t('clear')}
      </Button>
      <span className="ml-auto flex items-center gap-2">{children}</span>
    </>
  );
}

/**
 * Picking resources from the database: the parts a host composes into its own chrome (a split
 * view column, a dialog). The picker knows nothing of what the pick is for: the host says what a
 * result's check means and what opening one does.
 */
export const ResourcePicker = {
  Header: ResourcePickerHeader,
  Toolbar: ResourcePickerToolbar,
  Summary: ResourcePickerSummary,
  ViewSwitch: ResourcePickerViewSwitch,
  Count: ResourcePickerCount,
  Results: ResourcePickerResults,
  Inspector: ResourcePickerInspector,
  PickedFooter: ResourcePickerPickedFooter,
  Commit: ResourcePickerCommit,
  Actions: ResourcePickerActions
};

export type { ResourcePickerView };
